import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of, startWith } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { FetchAllBrands } from 'src/app/features/brands/store/brands.actions';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';

@Component({
  selector: 'app-edit-basic-product-infos',
  templateUrl: './edit-basic-product-infos.component.html',
  styleUrls: ['./edit-basic-product-infos.component.scss']
})
export class EditBasicProductInfosComponent implements OnInit {

  form!: FormGroup;
  brands$: Observable<IBrand[]> = of([]);
  filteredBrands$!: Observable<IBrand[]>;
  brand_id!: number;
  brand: IBrand | null = null;
  extra_identifiers: string[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  constructor(
    public dialogRef: MatDialogRef<EditBasicProductInfosComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions
  ) {
    this.brands$ = this.store.select((state) => [...state.brands.allBrands].sort((a, b) => a.name.localeCompare(b.name)));
   }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: new FormControl(this.data?.name ?? '', Validators.required),
      brandControl: new FormControl(this.data?.brand?.name ?? ''),
      description: new FormControl(this.data?.description),
      sku: new FormControl(this.data?.sku ?? '', Validators.required),
      barcode: new FormControl(this.data?.barcode),
      status: new FormControl(this.data?.status ?? ''),
      extra_identifiers: new FormControl(this.data?.extra_identifiers ?? []),
      expiry_date: new FormControl(this.data?.expiry_date ?? ''),
      hasExpirationDate: new FormControl(this.data?.expiry_date ? true : false)
    });

    this.extra_identifiers = this.data?.extra_identifiers ?? [];

    this.form.get('brandControl')?.valueChanges.subscribe(value => {
      if (typeof value === 'string' && value.trim() === '') {
        this.brand_id = 0
        this.brand = null
        this.form.get('brandControl')?.setValue(null, { emitEvent: false });
      }
    });

    if (this.data?.description) {
      const cleanDescription = this.stripHtml(this.data.description);
      this.form.patchValue({
        description: cleanDescription
      });
    }

    this.brand_id = this.data.brand_id;
    this.brand = this.data.brand;

    this.filteredBrands$ = combineLatest([
      this.brands$,
      this.form.get('brandControl')!.valueChanges.pipe(startWith('')),
    ]).pipe(map(([brands, input]) => this._filterStrings(brands, input)));
  }

  private _filterStrings(strings: any[], value: string): any[] {
    const filterValue = this._normalizeValue(value);
    return strings.filter((string) =>
      this._normalizeValue(string.name)?.includes(filterValue!)
    );
  }
  private _normalizeValue(value: string | undefined): string | undefined {
    return value?.toLowerCase().replace(/\s/g, '');
  }

  private stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').trim();
  }

  addExtraIdentifier(event: MatChipInputEvent): void {
    const input = (event.value || '').trim();
    if (input && !this.extra_identifiers.some((extraIdentifier) => extraIdentifier === input)) {
      const newExtraIdentifier = input;
      this.extra_identifiers.push(newExtraIdentifier);
      this.form.get('extra_identifiers')?.setValue([...this.extra_identifiers]);
    }
    event.chipInput?.clear();
  }


  removeExtraIdentifier(extraIdentifierToRemove: string): void {
    this.extra_identifiers = this.extra_identifiers.filter((extraIdentifier) => extraIdentifier !== extraIdentifierToRemove);
    this.form.get('extra_identifiers')?.setValue([...this.extra_identifiers]);
  }


  save(): boolean | void{
    if(!this.form.valid){
      this.form.markAllAsTouched();
      return false
    };
    const result = {
      // remove elements from the form value
      ...this.form.value,
      brandControl: undefined,
      hasExpirationDate: undefined,
      brand: undefined,
      brand_id: this.brand_id,
    }
    this.dialogRef.close(result)
  }

  onBrandSelected(brand: IBrand) {
    if (brand) {
      this.brand_id = brand.id;
      this.brand = brand;
    }
  }

  toggleExpirationDate(event: any) {
    const checkbox = event.checked
    
    if (checkbox) {
      this.form.get('hasExpirationDate')?.setValue(true);
      this.form.get('expiry_date')?.setValue(this.data?.expiry_date ?? '');
      this.form
        .get('expiry_date')
        ?.setValidators([Validators.required]);
    } else {
      this.form.get('hasExpirationDate')?.setValue(false);
      this.form.get('expiry_date')?.clearValidators();
      this.form.get('expiry_date')?.setValue('');
      // Reset the touched and pristine states to hide validation messages
      this.form.get('expiry_date')?.markAsUntouched();
      this.form.get('expiry_date')?.markAsPristine();
    }
    this.form.get('expiry_date')?.updateValueAndValidity();
    this.form.get('hasExpirationDate')?.updateValueAndValidity();
  }

  cancel(){
    this.dialogRef.close()
  }

}
